import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

/**
 * Listens for `markaii:quota-event` CustomEvents fired by `api/client.js`
 * on 429 / 402 responses. Shows a sticky banner with an "Upgrade plan"
 * CTA. Dismisses on click-away or after 10 seconds.
 *
 * Per ADR-0015 §1+§2: 429 = quota or rate limit; 402 = trial expired.
 */
export default function QuotaBanner() {
  const [event, setEvent] = useState(null);

  useEffect(() => {
    function onEvent(e) {
      setEvent(e.detail);
      const t = setTimeout(() => setEvent(null), 10_000);
      return () => clearTimeout(t);
    }
    window.addEventListener('markaii:quota-event', onEvent);
    return () => window.removeEventListener('markaii:quota-event', onEvent);
  }, []);

  if (!event) return null;

  const body = event.body || {};
  const isTrial = event.status === 402 || body.error === 'trial_expired';
  const isRateLimit = body.error === 'rate_limited';
  // System-wide hard ceiling — Agency tier or higher hit the 15-brand
  // cap. There is no self-serve upgrade; only path is sales contact.
  const isHardCap = body.error === 'hard_cap_contact_sales';
  // Agency customer at their tier limit (10 brands). Self-serve upgrade
  // isn't an option (Agency IS the top tier); CTA is contact-sales.
  const isAgencyTopOut = body.kind === 'project_create' && body.contact_sales_url && !isHardCap;
  const kind = body.kind || body.label;

  let headline, sub;
  if (isTrial) {
    headline = 'Your trial has ended';
    sub = 'Upgrade to keep publishing and generating posts.';
  } else if (isRateLimit) {
    headline = 'Slow down a moment';
    sub = `Hit the rate limit (${body.layer || 'global'}). Try again in ${body.retry_after_seconds || 60} seconds.`;
  } else if (isHardCap) {
    headline = `You've hit the ${body.hard_cap || 15}-brand self-serve ceiling`;
    sub = body.detail || 'Beyond this limit we set up a custom contract — drop us a line.';
  } else if (isAgencyTopOut) {
    headline = `You're at your Agency tier limit (${body.used}/${body.limit} brands)`;
    sub = `Need more? Agency goes up to ${body.hard_cap || 15} brands via a custom contract.`;
  } else {
    const limit = body.limit ? ` (${body.used}/${body.limit})` : '';
    headline = `You hit the ${kindLabel(kind)} limit${limit}`;
    sub = body.resets_at
      ? `Resets ${new Date(body.resets_at).toLocaleString()}. Upgrade your plan to lift the cap.`
      : 'Upgrade your plan to lift the cap.';
  }

  const showContactSales = (isHardCap || isAgencyTopOut) && body.contact_sales_url;
  const showUpgrade = !isRateLimit && !isHardCap && !isAgencyTopOut;

  return (
    <div className="quota-banner" role="status">
      <div className="quota-banner-body">
        <strong>{headline}</strong>
        <span>{sub}</span>
      </div>
      <div className="quota-banner-actions">
        {showContactSales && (
          <a
            href={body.contact_sales_url}
            className="btn btn-primary btn-small"
            target="_blank"
            rel="noreferrer"
          >
            Contact sales
          </a>
        )}
        {showUpgrade && (
          <Link to="/account/billing" className="btn btn-primary btn-small">
            Upgrade plan
          </Link>
        )}
        <button className="btn btn-small" onClick={() => setEvent(null)}>Dismiss</button>
      </div>
    </div>
  );
}

function kindLabel(kind) {
  switch (kind) {
    case 'post_create':     return 'monthly posts';
    case 'project_create':  return 'projects';
    case 'social_connect':  return 'connected social accounts';
    case 'ai_call':         return 'daily AI calls';
    default:                return kind || 'plan';
  }
}
